import Phaser from "phaser";
import type { ArtFrame } from "./tradeArtLayout";
import { harbourDrift, streamDrop } from "./tradeWaterfrontMotion";
import { TradeWaterfalls } from "./tradeWaterfalls";

// The streams are measured on the approved waterfront bitmap. Their endpoints
// stay on painted water, not over roofs, labels or arbitrary glowing circles.
const STREAMS = [
  [.267,.611,.270,.668], [.201,.614,.205,.645], [.365,.638,.367,.67],
] as const;
const LIGHTS = [[.252,.406],[.719,.414],[.792,.414],[.729,.683],[.83,.682]] as const;

export class TradeWaterfrontEffects {
  readonly drops: Phaser.GameObjects.Image[] = [];
  readonly lights: Phaser.GameObjects.Image[] = [];
  readonly wakes: Phaser.GameObjects.Graphics[] = [];
  readonly skiff?: Phaser.GameObjects.Image;
  readonly splash: Phaser.GameObjects.Graphics;
  readonly waterfalls: TradeWaterfalls;
  frame: ArtFrame = { x: 0, y: 0, width: 1, height: 1 };
  boatY = 0;
  surfaceWidth = 1;
  splashAt = -Infinity;
  splashX = 0;
  splashY = 0;
  clock = 0;

  constructor(readonly scene: Phaser.Scene, readonly ratio: number, readonly reduced: boolean) {
    this.waterfalls = new TradeWaterfalls(scene, reduced);
    // One cached particle texture for all droplets and window lights. This is
    // a soft optical effect, not a replacement illustration or building mask.
    const canvas = scene.textures.createCanvas("trade-water-drop", 24, 24);
    const context = canvas?.context;
    if (context && canvas) {
      const glow = context.createRadialGradient(12, 12, 0, 12, 12, 12);
      glow.addColorStop(0, "rgba(255,255,255,1)"); glow.addColorStop(.3, "rgba(217,255,255,.75)"); glow.addColorStop(1, "rgba(217,255,255,0)");
      context.fillStyle = glow; context.fillRect(0, 0, 24, 24); canvas.refresh();
    }
    // Below the island: fine reflected sunlight cannot spill over architecture.
    if (!reduced) {
      for (let i = 0; i < 9; i++) {
        const wake = scene.add.graphics().lineStyle(.8 * ratio, 0xeaffff, .8)
          .lineBetween(-10 * ratio, 0, -3 * ratio, -ratio).lineBetween(-3 * ratio, -ratio, 8 * ratio, 0)
          .lineBetween(-4 * ratio, 3 * ratio, 5 * ratio, 3 * ratio).setDepth(1);
        this.wakes.push(wake);
      }
    }
    if (scene.textures.exists("trade-skiff")) this.skiff = scene.add.image(0, 0, "trade-skiff").setDepth(2);
    if (!reduced) {
      STREAMS.forEach(() => {
        for (let i = 0; i < 7; i++) this.drops.push(scene.add.image(0, 0, "trade-water-drop").setDepth(4).setAlpha(0));
      });
      LIGHTS.forEach(() => this.lights.push(scene.add.image(0, 0, "trade-water-drop").setTint(0xffd394).setDepth(4)));
    }
    this.splash = scene.add.graphics().setDepth(2);
  }

  layout(frame: ArtFrame, surfaceWidth: number, platformY: number, seaTop: number) {
    this.frame = frame; this.surfaceWidth = surfaceWidth;
    this.waterfalls.layout(frame);
    this.boatY = Math.max(platformY - 42 * this.ratio, seaTop + 25 * this.ratio);
    this.skiff?.setDisplaySize(42 * this.ratio, 42 * this.ratio);
    this.drops.forEach(drop => drop.setDisplaySize(Math.max(1.2 * this.ratio, frame.width * .005), Math.max(4 * this.ratio, frame.height * .012)));
    this.lights.forEach((light, i) => {
      const point = LIGHTS[i]; if (!point) return;
      light.setPosition(frame.x + frame.width * point[0], frame.y + frame.height * point[1])
        .setDisplaySize(frame.width * .022, frame.width * .032);
    });
    this.update(this.clock);
  }

  touchWater(x: number, y: number) {
    if (this.reduced || this.clock - this.splashAt < 220) return;
    this.splashX = x; this.splashY = y; this.splashAt = this.clock;
  }

  update(time: number) {
    this.clock = time;
    this.waterfalls.update(time);
    const { x, y, width, height } = this.frame;
    const drift = harbourDrift(this.reduced ? 0 : time);
    this.skiff?.setPosition(this.surfaceWidth * drift.x, this.boatY + drift.y * this.ratio).setAngle(drift.angle);
    this.wakes.forEach((wake, i) => {
      const phase = time / 5000 + i * 1.7;
      // Behind the opaque island; reflections remain visible only over sea.
      wake.setPosition(x + width * (.08 + (i % 3) * .41) + Math.sin(phase) * 3 * this.ratio,
        y + height * (.37 + Math.floor(i / 3) * .27)).setAlpha(.12 + .22 * (.5 + .5 * Math.sin(phase)));
    });
    this.drops.forEach((drop, i) => {
      const stream = STREAMS[Math.floor(i / 7)]; if (!stream) return;
      const { progress, opacity } = streamDrop(time, i, 1500 + Math.floor(i / 7) * 90);
      drop.setPosition(x + width * (stream[0] + (stream[2] - stream[0]) * progress + Math.sin(i * 2.4) * .005),
        y + height * (stream[1] + (stream[3] - stream[1]) * progress))
        .setAlpha(opacity);
    });
    this.lights.forEach((light, i) => light.setAlpha(.23 + .13 * Math.sin(time / 1900 + i * 1.8)));
    this.splash.clear();
    const age = (time - this.splashAt) / 950;
    if (age >= 0 && age <= 1) {
      this.splash.lineStyle(this.ratio, 0xe6ffff, (1 - age) * .65)
        .strokeEllipse(this.splashX, this.splashY, (12 + age * 42) * this.ratio, (4 + age * 15) * this.ratio);
    }
  }
}
